import React, { useEffect, useMemo, useState } from 'react';
import { Form, Modal, Select, Spin, message } from 'antd';
import { freightAPI, userAPI } from '../api';

const FreightAvailableUsersDialog = ({ visible, template, onClose, onSaveSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionMap, setUserOptionMap] = useState({});
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const selectedUserIds = Form.useWatch('available_user_ids', form);

  const normalizedSelectedUserIds = useMemo(() => selectedUserIds || [], [selectedUserIds]);

  const formatUserLabel = (user) => {
    if (!user) return '';
    const detail = user.name || user.email;
    return detail ? `${user.username} (${detail})` : user.username;
  };

  const userSelectOptions = useMemo(() => {
    const mergedUsers = [...userOptions];

    normalizedSelectedUserIds.forEach((userId) => {
      const matchedUser = userOptionMap[userId];
      if (matchedUser && !mergedUsers.some((user) => user?.id === userId)) {
        mergedUsers.push(matchedUser);
      }
    });

    return mergedUsers
      .filter(Boolean)
      .map((user) => ({
        value: user.id,
        label: formatUserLabel(user),
      }));
  }, [normalizedSelectedUserIds, userOptionMap, userOptions]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const availableUserIds = template?.available_user_ids || [];
    const availableUsers = template?.available_users || [];
    const nextMap = {};

    availableUsers.forEach((user) => {
      nextMap[user.id] = user;
    });

    setUserSearchInput('');
    setUserOptions(availableUsers);
    setUserOptionMap(nextMap);
    form.setFieldsValue({
      available_user_ids: availableUserIds,
    });
  }, [visible, template, form]);

  const handleUserSearch = async (value) => {
    const query = (value || '').trim();
    setUserSearchInput(value);

    if (!query) {
      setUserOptions([]);
      return;
    }

    try {
      setUserSearchLoading(true);
      const response = await userAPI.getAll({
        page: 1,
        page_size: 20,
        role: 'dropshipper',
        search: query,
      });
      const userList = (response.data?.data || []).filter((user) => user.role === 'dropshipper');
      setUserOptions(userList);
      setUserOptionMap((prev) => {
        const next = { ...prev };
        userList.forEach((user) => {
          next[user.id] = user;
        });
        return next;
      });
    } catch (error) {
      message.error('搜索用户失败');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!template?.id) {
      message.error('未找到要编辑的运费模板');
      return;
    }

    try {
      setLoading(true);
      await freightAPI.updateAvailableUsers(template.id, {
        available_user_ids: values.available_user_ids || [],
      });
      message.success('更新适用用户成功');
      onSaveSuccess();
    } catch (error) {
      message.error(error.response?.data?.detail || '更新适用用户失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={template ? `编辑适用用户 - ${template.name}` : '编辑适用用户'}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="保存"
      cancelText="取消"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="适用用户"
          name="available_user_ids"
          extra="留空表示所有用户可用"
        >
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="输入用户名搜索并选择指定用户"
            value={normalizedSelectedUserIds}
            options={userSelectOptions}
            filterOption={false}
            onSearch={handleUserSearch}
            onChange={(value) => {
              form.setFieldsValue({ available_user_ids: value || [] });
            }}
            notFoundContent={userSearchLoading ? <Spin size="small" /> : (userSearchInput ? '未找到匹配用户' : '输入关键词搜索用户')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FreightAvailableUsersDialog;
